import React, { useState, useEffect, useRef } from "react";
import EditableText from "../editor/editableText";
import _ from "lodash";
import css from "../../assets/styles/shared/editor.module.scss";
import pollsCss from "../../assets/styles/polls/polls.module.scss";
import {
  Button,
  Confirm,
  Form,
  Header,
  Icon,
  TextArea,
} from "semantic-ui-react";
import PollEditableField from "./pollEditableField";
import { useNavigate, useParams } from "react-router";
import {
  CrossIconComponent,
  DeleteIconComponent,
  WarningIconComponent,
} from "../iconComponents";
import PreviewPoll from "./specs/PreviewPoll";
import { ClipLoader } from "react-spinners";
import { useDispatch } from "react-redux";
import EmojiPicker from "emoji-picker-react";
import { EyeIcon, SmileEmoji } from "../../assets/styles/polls/icons";
import CustomThankYouPage from "./specs/CustomThankYouPage";
import Gifs from "./specs/Gifs";
import { PollsEditor } from "./PollsEditor";
import moment from "moment-timezone";
import { CopyCode } from "./specs/CopyCode";

export default function Polls(props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [draft, setDraft] = useState(defaultValue);
  const [loading, setLoading] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [previewPollTab, setPreviewPollTab] = useState(false);
  const [previewThankYouPage, setPreviewThankYouPage] = useState(false);
  const [pollEmoji, setPollEmoji] = useState({ yes: false, no: false });
  const [gifsModal, setGifsModal] = useState(false);
  const [gifIndex, setGifIndex] = useState(0);
  const [triggerUpdate, setTriggerUpdate] = useState(false);
  const [copyCode, setCopyCode] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [activePollTab, setActivePollTab] = useState("poll");

  useEffect(() => {
    if (id) {
      props?.fetch();
    }
  }, [id]);

  useEffect(() => {
    if (id && props?.polls?.data?.length > 0) {
      const filterResponse = props?.polls?.data?.filter((poll) => {
        return Number.parseInt(poll?.id) === Number.parseInt(id);
      });

      setDraft({
        id: filterResponse?.[0]?.id,
        data: filterResponse?.[0] ?? defaultValue?.data,
      });
    }
  }, [id, props?.polls?.data, triggerUpdate]);

  const isSaveButtonDisabled = () => {
    const { title, ends_at, subaccount_id, template_data, poll_options } =
      draft.data;
    const isTitleEmpty = !title || title.trim() === "";
    const isEndsAtInvalid = !ends_at || isNaN(new Date(ends_at).getTime());
    const isSubaccountIdInvalid =
      typeof subaccount_id !== "number" || subaccount_id < 0;
    const isQuestionEmpty =
      !template_data?.question || template_data.question.trim() === "";
    const areOptionsInvalid = poll_options?.some((option) => {
      return (
        !option.archived &&
        (!option.option_template_data?.option_text ||
          option?.option_template_data?.option_text?.trim() === "")
      );
    });
    return (
      isTitleEmpty ||
      isEndsAtInvalid ||
      isSubaccountIdInvalid ||
      isQuestionEmpty ||
      areOptionsInvalid
    );
  };

  const handleSave = () => {
    props?.save?.(draft, navigate, setLoading);
  };

  const handleDraftChange = (key, value) => {
    setDraft((prev) => {
      const newDraft = _.cloneDeep(prev);
      _.set(newDraft, key, value);
      return newDraft;
    });
  };

  const getGifUrl = (url) => {
    setDraft((prev) => {
      const updatedOptions = [...prev?.data?.poll_options];
      updatedOptions[gifIndex].option_template_data.gif_url = url;

      return {
        ...prev,
        data: {
          ...prev?.data,
          poll_options: updatedOptions,
        },
      };
    });
    setGifsModal(false);
  };

  const renderPollType = () => {
    const sizeMultiplier =
      parseFloat(draft?.data?.template_data?.customization?.general?.size) || 1;
    return (
      <>
        <div className={pollsCss.mcqVariant}>
          {draft?.data?.poll_type === "multiple-choice" &&
            draft?.data?.template_data?.variant === "gif" && (
              <div style={{ marginTop: `${20 * sizeMultiplier}px` }}>
                <Gifs
                  setGifsModal={setGifsModal}
                  gifsModal={gifsModal}
                  getGifUrl={getGifUrl}
                />
              </div>
            )}
        </div>
      </>
    );
  };

  const previewButton = () => (
    <Button
      onClick={() => setPreviewPollTab(true)}
      className={pollsCss?.previewButton}
    >
      <EyeIcon /> Preview
    </Button>
  );

  const undoButton = () => (
    <Button
      className={pollsCss?.previewButton}
      onClick={() => setTriggerUpdate((prev) => !prev)}
    >
      <Icon name="undo" size="small" /> Reset
    </Button>
  );

  const getCode = () => (
    <>
      <Button onClick={() => setCopyCode(true)} className={css?.invertedButton}>
        Get Code
      </Button>
      <CopyCode
        copyCode={copyCode}
        setCopyCode={setCopyCode}
        draft={draft?.data}
        alignment={draft.data.template_data.customization.general.alignment}
      />
    </>
  );

  const cancelButton = () => {
    return (
      <Button
        onClick={() => navigate("/polls")}
        className={pollsCss?.cancelButton}
      >
        Cancel
      </Button>
    );
  };

  const saveButton = () => {
    const sizeMultiplier =
      parseFloat(draft?.data?.template_data?.customization?.general?.size) || 1;
    return (
      <Button
        onClick={handleSave}
        className={css?.primaryButton}
        disabled={isSaveButtonDisabled() || loading}
      >
        {loading ? (
          <div style={{ display: "flex", gap: `${3 * sizeMultiplier}px` }}>
            Save
            <ClipLoader color="#fff" size={15 * sizeMultiplier} />
          </div>
        ) : (
          "Save"
        )}
      </Button>
    );
  };

  const renderActionButtons = () => (
    <div className={pollsCss?.pollsActionButton}>
      {previewButton()}
      {draft?.id !== "new" && undoButton()}
      {draft?.id !== "new" && getCode()}
      {cancelButton()}
      {saveButton()}
    </div>
  );

  const renderEditableText = () => (
    <div className={css?.editorActions}>
      <EditableText
        name={draft?.data?.title}
        onChange={(value) => handleDraftChange("data.title", value)}
      />
      {renderActionButtons()}
    </div>
  );

  const handleAddOption = () => {
    const newOption = {
      archived: false,
      option_template_data: {
        option_text: "",
        poll_type_variant: "multiple-choice",
      },
    };

    setDraft((prev) => {
      const updatedPollOptions = [...(prev?.data?.poll_options || [])];
      updatedPollOptions.push(newOption);
      return {
        ...prev,
        data: {
          ...prev?.data,
          poll_options: updatedPollOptions,
        },
      };
    });
  };

  const textAreaRef = useRef(null);

  const autoHeight = (e) => {
    const textArea = e.target;
    if (textArea) {
      textArea.style.height = "auto";
      textArea.style.height = `${textArea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    if (textAreaRef.current) {
      const textArea = textAreaRef.current.ref.current;
      autoHeight({ target: textArea });
    }
  }, [
    draft?.data?.template_data?.question,
    previewThankYouPage,
    previewPollTab,
  ]);

  const pollQuestion = () => {
    const sizeMultiplier =
      parseFloat(draft?.data?.template_data?.customization?.general?.size) || 1;
    return (
      <div
        style={{
          textAlign: "center",
          backgroundColor: `#${draft?.data?.template_data?.customization?.question?.questionBackground}`,
          borderRadius: `${8 * sizeMultiplier}px`,
          padding: `${10 * sizeMultiplier}px`,
          marginBottom: `10px`,
        }}
        className={pollsCss?.inputFields}
      >
        <TextArea
          autoFocus
          ref={textAreaRef}
          name={draft?.data?.template_data?.question}
          placeholder="Write your question here"
          onChange={(e) => {
            handleDraftChange("data.template_data.question", e?.target?.value);
            autoHeight(e);
          }}
          defaultValue={draft?.data?.template_data?.question}
          className={pollsCss.editableTextArea}
          style={{
            fontSize: `${1.5 * sizeMultiplier}rem`,
            color: `#${draft?.data?.template_data?.customization?.question?.text}`,
            fontFamily:
              draft?.data?.template_data?.customization?.question?.font,
            padding: `0px`,
          }}
          rows={1}
        />
      </div>
    );
  };

  const handleRemoveOption = (index) => {
    setDraft((prev) => {
      const updatedDraft = _.cloneDeep(prev);
      if (updatedDraft?.data?.poll_options) {
        updatedDraft.data.poll_options[index].archived = true;
      } else {
        console.error("Invalid index or poll_options structure.");
      }

      return updatedDraft;
    });
  };

  const renderMultipleChoiceOptions = () => {
    let displayIndex = 0;

    return (
      <>
        {draft?.data?.poll_options?.map((option, actualIndex) => {
          if (option?.archived) return null;

          const alphabetIndex = displayIndex++;
          const currentIndex = actualIndex;
          const sizeMultiplier =
            parseFloat(
              draft?.data?.template_data?.customization?.general?.size
            ) || 1;

          return (
            <div
              className={`${
                draft?.data?.template_data?.variant === "gif"
                  ? pollsCss.answerChoices
                  : pollsCss.mcqChoice
              }`}
              style={{
                fontSize: `${1 * sizeMultiplier}rem`,
                backgroundColor:
                  draft?.data?.template_data?.variant !== "gif" &&
                  `#${draft?.data?.template_data?.customization?.answer?.questionBackground}`,
                border:
                  draft?.data?.template_data?.variant !== "gif" &&
                  `1px solid #${draft?.data?.template_data?.customization?.answer?.border}`,
                padding: `${8 * sizeMultiplier}px ${15 * sizeMultiplier}px`,
                margin: `${15 * sizeMultiplier}px 0`,
              }}
            >
              {draft?.data?.template_data?.variant === "gif" && (
                <Header as="h4" className={pollsCss.labels}>
                  Answer Choice {alphabetIndex + 1}
                </Header>
              )}
              <div
                key={currentIndex}
                className={pollsCss?.optionsInput}
                style={{
                  width:
                    draft?.data?.template_data?.variant === "gif" && "100%",
                }}
              >
                {draft?.data?.template_data?.variant === "gif" && (
                  <div>
                    {draft?.data?.poll_options.map((url, index) => {
                      return (
                        currentIndex === index && (
                          <div>
                            <div
                              className={pollsCss.gif_img}
                              style={{
                                width: `${170 * sizeMultiplier}px`,
                                height: `${120 * sizeMultiplier}px`,
                              }}
                            >
                              <span
                                className={`${pollsCss?.optionNo} ${pollsCss.gifNo}`}
                              >
                                {String.fromCharCode(65 + alphabetIndex)}
                              </span>
                              <img src={url.option_template_data.gif_url} />
                              {draft?.data?.template_data?.variant === "gif" &&
                                !url.option_template_data.gif_url && (
                                  <Button
                                    className={`${pollsCss?.addOptionBtn} ${pollsCss.addGifButton}`}
                                    onClick={() => {
                                      setGifsModal(true);
                                      setGifIndex(currentIndex);
                                    }}
                                    style={{
                                      fontSize: `${1 * sizeMultiplier}rem`,
                                    }}
                                  >
                                    <Icon name="plus" /> Add GIF
                                  </Button>
                                )}
                            </div>
                          </div>
                        )
                      );
                    })}
                  </div>
                )}
                {draft?.data?.template_data?.variant !== "gif" && (
                  <span className={pollsCss?.optionNo}>
                    {String.fromCharCode(65 + alphabetIndex)}
                  </span>
                )}
                <div className={pollsCss.fieldInput}>
                  <PollEditableField
                    onChange={(value) =>
                      setDraft((prev) => {
                        const updatedOptions = [...prev?.data?.poll_options];
                        updatedOptions[
                          currentIndex
                        ].option_template_data.option_text = value;

                        return {
                          ...prev,
                          data: {
                            ...prev?.data,
                            poll_options: updatedOptions,
                          },
                        };
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleAddOption();
                        setFocusedIndex(currentIndex + 1);
                      }
                    }}
                    autoFocus={currentIndex === focusedIndex}
                    name={option?.option_template_data?.option_text}
                    placeholder="Type answer choice"
                    style={{
                      fontSize: `${1 * sizeMultiplier}rem`,
                      color: `#${
                        draft?.data?.template_data?.customization?.answer
                          ?.text || "000000"
                      }`,
                      fontFamily:
                        draft?.data?.template_data?.customization?.answer?.font,
                      border:
                        draft?.data?.template_data?.variant !== "gif"
                          ? "none"
                          : `1px solid #${draft?.data?.template_data?.customization?.answer?.border}`,
                      background: `#${draft?.data?.template_data?.customization?.answer?.questionBackground}`,
                      padding: `${8 * sizeMultiplier}px`,
                    }}
                  />
                </div>
                <Confirm
                  content="Are you sure you want to delete?"
                  open={isDelete === currentIndex}
                  onCancel={() => setIsDelete(null)}
                  onConfirm={() => {
                    setIsDelete(null);
                    handleRemoveOption(currentIndex);
                  }}
                  confirmButton="Yes"
                />
                {draft?.data?.poll_options?.length > 1 && (
                  <span
                    onClick={() => setIsDelete(currentIndex)}
                    style={{
                      background: "transparent",
                      cursor: "pointer",
                    }}
                  >
                    <DeleteIconComponent />
                  </span>
                )}
              </div>
            </div>
          );
        })}
        <Button className={pollsCss?.addOptionBtn} onClick={handleAddOption}>
          Add answer choice
        </Button>
      </>
    );
  };

  const renderYesNoOptions = () => {
    const yesIndex = draft?.data?.poll_options?.findIndex(
      (option) =>
        option?.option_template_data?.option_text === "Yes" &&
        option?.archived === false
    );

    const noIndex = draft?.data?.poll_options?.findIndex(
      (option) =>
        option?.option_template_data?.option_text === "No" &&
        option?.archived === false
    );

    const sizeMultiplier =
      parseFloat(draft?.data?.template_data?.customization?.general?.size) || 1;

    return (
      <div>
        {draft.data.template_data.variant === "emoji" && (
          <Header as="h4">Pick an Emoji</Header>
        )}
        <div
          className={pollsCss?.yesNoBtn}
          style={{
            border: `1px solid #${draft?.data?.template_data?.customization?.answer?.border}`,
            width: `${220 * sizeMultiplier}px`,
            height: `${35 * sizeMultiplier}px`,
          }}
        >
          {draft?.data?.poll_options?.map((option, index) => {
            if (option.archived) return null;
            return (
              <a
                key={index}
                style={{
                  color: `#${draft?.data?.template_data?.customization?.answer?.text}`,
                  background: `#${draft?.data?.template_data?.customization?.answer?.questionBackground}`,
                  fontFamily: `${draft?.data?.template_data?.customization?.answer?.font}`,
                  fontSize:
                    draft.data.template_data.variant === "emoji"
                      ? `${20 * sizeMultiplier}px`
                      : `${14 * sizeMultiplier}px`,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 0,
                }}
                className={pollsCss?.yesOption}
              >
                {draft.data.template_data.variant === "emoji"
                  ? option?.option_template_data?.emoji
                  : option?.option_template_data?.option_text}
                {draft.data.template_data.variant === "emoji" && (
                  <span
                    onClick={() =>
                      setPollEmoji({
                        yes: index === yesIndex ? !pollEmoji?.yes : false,
                        no: index === noIndex ? !pollEmoji?.no : false,
                      })
                    }
                    className={pollsCss.emoji_class}
                  >
                    <SmileEmoji />
                  </span>
                )}
              </a>
            );
          })}
        </div>
        {draft.data.template_data.variant === "emoji" && (
          <div style={{ display: "flex", gap: `${10 * sizeMultiplier}px` }}>
            {pollEmoji?.yes && yesIndex !== -1 && (
              <EmojiPicker
                onEmojiClick={(emojiObject) => {
                  handleDraftChange(
                    `data.poll_options[${yesIndex}].option_template_data.emoji`,
                    emojiObject.emoji
                  );
                  setPollEmoji({ yes: false, no: false });
                }}
              />
            )}
            {pollEmoji?.no && noIndex !== -1 && (
              <EmojiPicker
                onEmojiClick={(emojiObject) => {
                  handleDraftChange(
                    `data.poll_options[${noIndex}].option_template_data.emoji`,
                    emojiObject.emoji
                  );
                  setPollEmoji({ yes: false, no: false });
                }}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const renderRatingOption = () => {
    let initialIndex = 0;
    const sizeMultiplier =
      parseFloat(draft?.data?.template_data?.customization?.general?.size) || 1;
    return (
      <>
        <div
          className={pollsCss.ratingScale}
          style={{
            border: `none`,
            padding: `${10 * sizeMultiplier}px`,
            margin: `${5 * sizeMultiplier}px 0`,
          }}
        >
          {draft?.data?.poll_options?.map((scale, index) => {
            if (scale.archived) return null;

            const fontSize = !draft.data.template_data.translateToEmojis
              ? `${14 * sizeMultiplier}px`
              : `${(12 + initialIndex * 2) * sizeMultiplier}px`;

            initialIndex = initialIndex + 1;
            return (
              <div
                key={index}
                className={pollsCss.ratingNumbers}
                style={{
                  fontSize,
                  color: `#${draft?.data?.template_data?.customization?.answer?.text}`,
                  background: `#${draft?.data?.template_data?.customization?.answer?.questionBackground}`,
                  fontFamily: `${draft?.data?.template_data?.customization?.answer?.font}`,
                  border: `1px solid #${draft?.data?.template_data?.customization?.answer?.border}`,
                  padding: `${10 * sizeMultiplier}px`,
                  height: `${35 * sizeMultiplier}px`,
                  width: `${35 * sizeMultiplier}px`,
                }}
              >
                {draft.data.template_data.translateToEmojis
                  ? scale.option_template_data.emoji
                  : scale.option_template_data.option_text}
              </div>
            );
          })}
        </div>
        {draft?.data?.poll_type === "rating" && (
          <p className={pollsCss?.infoText} style={{ textAlign: "left" }}>
            1 - the worst, 10 - the best
          </p>
        )}
      </>
    );
  };

  const pollOptions = () => {
    return (
      <>
        {draft?.data?.poll_type === "multiple-choice" &&
          renderMultipleChoiceOptions()}
        {draft?.data?.poll_type === "yes-no" && renderYesNoOptions()}
        {draft?.data?.poll_type === "rating" && renderRatingOption()}
      </>
    );
  };

  const previewPoll = () => {
    return (
      <>
        <div className={pollsCss.previewHeader}>
          <p>
            <WarningIconComponent /> Preview mode
          </p>
          <button onClick={() => setPreviewPollTab(false)}>
            Close preview <CrossIconComponent />
          </button>
        </div>
        <PreviewPoll draft={draft?.data} />;
      </>
    );
  };

  const edithankYouPage = () => {
    return (
      <>
        <CustomThankYouPage
          draft={draft}
          handleDraftChange={handleDraftChange}
        />
      </>
    );
  };
  const sizeMultiplier =
    parseFloat(draft?.data?.template_data?.customization?.general?.size) || 1;
  return (
    <>
      {previewPollTab && previewPoll()}
      {previewThankYouPage && edithankYouPage()}
      {!previewThankYouPage && !previewPollTab && (
        <div className={`${css?.editorContainer}`}>
          <Form className={`${css?.editorOptions} ${pollsCss.editorOptions}`}>
            <PollsEditor draft={draft} setDraft={setDraft} />
            {renderPollType()}
          </Form>
          <div className={css?.editorTimer}>
            {renderEditableText()}
            <div className={pollsCss?.pollsOptions}>
              <div className={pollsCss.pollsOptionsHeader}>
                <button
                  className={
                    activePollTab === "poll" ? pollsCss.activeButton : ""
                  }
                  onClick={() => setActivePollTab("poll")}
                >
                  Poll{" "}
                </button>
                <button
                  className={
                    activePollTab === "thankYou" ? pollsCss.activeButton : ""
                  }
                  onClick={() => setActivePollTab("thankYou")}
                >
                  Thank You Page
                </button>
              </div>
              <div className={pollsCss?.pollsContainer}>
                {activePollTab === "poll" && (
                  <table
                    align={
                      draft?.data?.template_data?.customization.general
                        ?.alignment
                    }
                    style={{
                      background: `#${draft?.data?.template_data?.customization.general.bodyBackgroundColor}`,
                      width:
                        draft.data.poll_type === "multiple-choice" &&
                        draft?.data?.template_data?.variant === "gif"
                          ? `${500 * sizeMultiplier}px`
                          : `${340 * sizeMultiplier}px`,
                      padding: `${20 * sizeMultiplier}px`,
                      margin: `${10 * sizeMultiplier}px auto`,
                    }}
                  >
                    {pollQuestion()}
                    {pollOptions()}
                  </table>
                )}
                {activePollTab === "thankYou" && edithankYouPage()}
              </div>
            </div>
            {draft?.data?.poll_type === "yes-no" && (
              <p className={pollsCss?.infoText} style={{ textAlign: "center" }}>
                Click on smiling face to change text into emoji
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const defaultValue = {
  id: "new",
  data: {
    title: "New Polls",
    ends_at: moment().add(7, "days").toISOString(),
    subaccount_id: 0,
    archived: false,
    total_responses: 0,
    poll_type: "multiple-choice",
    template_data: {
      question: "",
      uniqueUserCode: false,
      gifLableVisible: false,
      resultOnThankYouPage: false,
      translateToEmojis: false,
      isPollExpired: false,
      variant: "text",
      hasEndDate: false,
      timeZone: moment.tz.guess(),
      closingText: "This poll is now closed. Thank you for your interest!",
      customization: {
        general: {
          bodyBackgroundColor: "ffffff",
          size: "1.2",
          alignment: "center",
        },
        question: {
          font: "Lato",
          text: "000000",
          questionBackground: "ffffff",
        },
        answer: {
          font: "Lato",
          text: "000000",
          questionBackground: "ffffff",
          border: "edeff1",
        },
      },
      thankYouPage: {
        title: "Thank You",
        description: "Your feedback is valuable for us",
        icon: "",
        basePath: `${process.env.REACT_APP_DOMAIN_URL}/image/`,
        backgroundColor: "ffffff",
        text: "606062",
      },
      ratingScale: {
        min: 1,
        max: 10,
      },
    },
    poll_options: [
      {
        archived: false,
        option_template_data: {
          option_text: "",
          gif_url: "",
          emoji: "👍",
          poll_type_variant: "multiple-choice",
        },
      },
    ],
  },
};
